import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AnalysisApiService } from 'src/app/services/api/analysis.api.service';
import { StorageService } from 'src/app/services/storage.service';
import { UserApiService } from 'src/app/services/api/user.api.service';
import { IResponseDownload } from 'src/app/model/response/response-download.interface';

interface IPreparedDownloadContent {
  url: SafeUrl;
  fileName: string;
}

@Component({
  selector: 'app-download',
  templateUrl: './download.component.html',
  styleUrls: ['./download.component.scss'],
})
export class DownloadComponent implements OnInit {
  isLoading = true;
  analysisId: string = null;
  data: IResponseDownload | null = null;
  clickedButtonOrderId: string | null = null;
  preparedDownloadUrls: { [key: string]: IPreparedDownloadContent } = {};
  availableServerForDownload: Array<string> = [];
  modalText: string = null;
  modal = false;
  error = false;
  downloadInProgress = false;

  private currentServer: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private analysisApiService: AnalysisApiService,
    private datePipe: DatePipe,
    private storageService: StorageService,
    private userApiService: UserApiService,
    private sanitizer: DomSanitizer,
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentServer = this.storageService.getJSON('SERVER');
    this.analysisId = this.route.snapshot.paramMap.get('analyseId');

    if (this.analysisId) {
      this.availableServerForDownload = await this.analysisApiService.checkWhereDownloadIsAvailable(
        this.analysisId,
      );
      this.handleServerAvailability();
      this.data = await this.getAnalysisData(this.analysisId);
      if (this.data) {
        this.isLoading = false;
      } else {
        this.setModalText('response.stl_download.error');
        this.modal = true;
        this.error = true;
      }
    }
  }

  private handleServerAvailability(): void {
    if (
      this.availableServerForDownload.length === 1 &&
      this.availableServerForDownload[0] !== this.currentServer
    ) {
      this.setModalText('response.stl_download.server_text_one');
      this.modal = true;
      this.userApiService.switchAPI(this.availableServerForDownload[0]);
      setTimeout(() => {
        location.reload();
      }, 4000);
      return;
    } else if (
      this.availableServerForDownload.length === 1 &&
      this.availableServerForDownload[0] === this.currentServer
    ) {
      this.modal = false;
    } else if (this.availableServerForDownload.length >= 2) {
      this.setModalText('response.stl_download.server_text_two');
      this.modal = true;
    } else {
      this.setModalText('response.stl_download.server_text_three');
      this.modal = true;
      setTimeout(() => {
        this.closeModal();
        return;
      }, 4000);
    }
  }

  closeModal() {
    this.modal = false;
  }

  setModalText(val: string) {
    this.modalText = val;
  }

  siteReload() {
    location.reload();
  }

  navigateHome() {
    this.navigate('/registration-requests');
  }

  private navigate(url: string): void {
    this.router.navigateByUrl(url);
  }

  async startDownload(orderId: string, orderProductId: string): Promise<void> {
    this.downloadInProgress = true;
    this.clickedButtonOrderId = orderProductId;

    try {
      const downloadData = await this.prepareDownload(orderId, orderProductId);
      if (downloadData) {
        this.preparedDownloadUrls[orderProductId] = downloadData;
        this.downloadInProgress = false;
        this.clickedButtonOrderId = null;
      }
    } catch (error) {
      console.error('Error during download preparation:', error);
      this.downloadInProgress = false;
      this.clickedButtonOrderId = null;
    }
  }

  private async getAnalysisData(id: string): Promise<IResponseDownload | null> {
    try {
      return await this.analysisApiService.getAnalysis(id);
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      return null;
    }
  }

  private async prepareDownload(
    orderId: string,
    orderProductId: string,
  ): Promise<IPreparedDownloadContent | null> {
    try {
      const result = await this.analysisApiService.getDownloadContent(
        this.analysisId,
        orderId,
        orderProductId,
      );
      if (!(result instanceof Blob)) {
        throw new Error('Expected a Blob');
      }
      const currentdate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
      const url = URL.createObjectURL(result);
      const sanitizedUrl = this.sanitizer.bypassSecurityTrustUrl(url) as SafeUrl;
      const fileName = `${this.analysisId}_${orderId}_${orderProductId}_${currentdate}.stl`;
      console.log('url: ', sanitizedUrl);
      return { url: sanitizedUrl, fileName };
    } catch (error) {
      console.error('Error preparing download content:', error);
      return null;
    }
  }
}
